import { Injectable } from '@angular/core';
import { ContentItem } from './content-item.model';
import { Insight } from '../contributions/insight.model';
import { InsightExtended } from '../contributions/insight-extended.model';
import { InsightResponse, IsoDateTimeString } from '../contributions/insight-response.model';
// import { CultureService } from '../cultures';
import { Tag } from '@frontend/core';
import { Observable, of, Subscription, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CultureService } from '../cultures';
import { ErrorService } from '../error';

@Injectable({
  providedIn: 'root'
})
export class InsightsService {

  insights: InsightExtended[] = [];

  constructor(
    private cultureService: CultureService,
    private http: HttpClient,
    private errorService: ErrorService,
  ) { }

  
  convertInsightToContentItem(insight: Insight): ContentItem {

    const culturesAsTags : Tag[] = insight.cultures?.length ? this.cultureService.convertCulturesToTags(insight.cultures,false) : [];


    return new ContentItem( // THIS IS VERY SIMPLE - WE NEED TO handle different kinds of insights with HTML body and other properties
      insight.id,
      insight.s,
      null,
      null,
      null,
      insight.m,
      null,
      null,
      insight.slug,
      null,
      null,
      insight.images ?? [],
      null,
      null,
      null,
      null,
      null,
      culturesAsTags,
      null,
      null
    )
  }

  /**
   * Normalises the wire payload of `api/v1/insights` into the frontend model: timestamps
   * become `Date`s, cultures get their `metonyms` split into an array, and a subject-less
   * insight (which the backend sends as an object with every field null) becomes `null`.
   *
   * The flat `images` array is disk-agnostic and passes through as-is; delivery URLs are
   * built at render time (imageForRole + imageUrl).
   */
  transformInsight (insightResponse : InsightResponse) : InsightExtended {
    return {
      ...insightResponse,
      date: this.toDate(insightResponse.date),
      released_at: this.toDate(insightResponse.released_at),
      created_at: this.toDate(insightResponse.created_at),
      updated_at: this.toDate(insightResponse.updated_at),
      subject: insightResponse.subject?.id ? insightResponse.subject : null,
      cultures: this.cultureService.transformCultures(insightResponse.cultures ?? []),
      translated_locales: insightResponse.translated_locales ?? { s: [], m: [], l: [] },
    };
  }

  private toDate (value : IsoDateTimeString | null) : Date | null {
    return value ? new Date(value) : null;
  }


  getInsight (id : number, freshFromBackend : boolean){

    let cachedInsight : InsightExtended | undefined;
    if (!freshFromBackend){
      cachedInsight = this.getCachedInsight(id);
    }
    if (cachedInsight){
      return of(cachedInsight);
    };

    return this.http.get<{ 'data': InsightResponse }>(
      'api/v1/insights/' + id)
      .pipe(
      map(response => {
        const insight: InsightExtended = this.transformInsight(response.data);
        this.cacheInsight(insight);
        return insight;
      }),
      catchError((error) => {
        return this.handleError(error);
      })
      )
  };
  getCachedInsight (id: number){
    return this.insights.find(t=>t.id === id);
  }
  cacheInsight (insight : InsightExtended){
    let cachedInsightIndex = this.insights.findIndex(t=>t.id === insight.id);
    if (cachedInsightIndex >-1){
      this.insights[cachedInsightIndex] = insight;
    } else {
      this.insights.push(insight);
    }
  };

  private handleError(errorResponse: HttpErrorResponse) {
    let errorMessage = 'error.something_went_wrong';
    if (!errorResponse.error || !errorResponse.error.message) {
      return throwError(errorMessage);
    } else {
      const message = errorResponse.error.message;
      const standardErrorMessageTranslationKey = this.errorService.getCommonErrorMessageTranslationKey(message);
      if(standardErrorMessageTranslationKey){
        errorMessage = standardErrorMessageTranslationKey;
      }
    }
    switch (errorResponse.error.message) {
      case 'This action is unauthorized.':
        errorMessage = 'error.permissions_lacking';
        break;
      default:
      // no action needed. We already set the default error message.
    }
    if (errorResponse.error.meta){
      return throwError({message:errorMessage,meta:errorResponse.error.meta});
    }
    return throwError(errorMessage);
  }

}
